// Purchase history (/purchases) — GET /orders (date, product, billed total, status).
// A pending order links back into checkout (/buy?order=<id>) so an unpaid QRIS can be
// RESUMED instead of duplicated; nothing here creates an order — /buy does that.
// A past-expiry order is rendered Kedaluwarsa (no link) from the stored expires_at
// IMMEDIATELY — the server row flips pending→expired only after a ~30-min settlement
// grace, and this list must not show a payable-looking dead QRIS in that window. The
// server also withholds `checkout` from pending orders of an already-entitled user, so a
// stale link here can never re-charge one.
// ≥640px: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/table.tsx — MIT (Table kit);
// <640px the same rows collapse to stacked label-over-value items (ui/DESIGN.md §5).

import { Link } from "react-router-dom";
import { dataClass } from "@ahcut/ui/recipes";
import {
  Badge,
  Card,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { listOrders, orderBilledTotal, orderDisplayStatus } from "@/lib/orders";
import { formatIDR, formatDate, orderStatusLabel, orderStatusTone, tierLabel } from "@/lib/format";
import type { Order, OrderStatus } from "@/lib/types";

interface OrderRow {
  id: string;
  date: string;
  product: string;
  /** The billed total (harga + fee QRIS when reported) — matches checkout and the bank statement. */
  total: string;
  status: OrderStatus;
  action: { to: string; label: string } | null;
}

function toRow(o: Order): OrderRow {
  const status = orderDisplayStatus(o);
  return {
    id: o.id,
    date: formatDate(o.created_at),
    product: `Lisensi ${tierLabel(o.tier)}`,
    total: formatIDR(orderBilledTotal(o).total),
    status,
    action:
      o.status === "paid"
        ? { to: `/purchases/${encodeURIComponent(o.id)}`, label: "Lihat kwitansi" } // receipt; "Kelola lisensi" lives there
        : status === "pending"
          ? { to: `/buy?order=${encodeURIComponent(o.id)}`, label: "Lanjutkan pembayaran" }
          : null,
  };
}

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <Badge tone={orderStatusTone(status)} dot>
      {orderStatusLabel(status)}
    </Badge>
  );
}

function RowAction({ action }: { action: OrderRow["action"] }) {
  if (action) return <Link to={action.to}>{action.label}</Link>;
  return (
    <>
      <span aria-hidden="true" className="text-fg-2">
        —
      </span>
      <span className="sr-only">Tidak ada aksi</span>
    </>
  );
}

export function Purchases() {
  const { data: orders, loading, error, reload } = useAsync<Order[]>((s) => listOrders(s), []);

  // Rendered in every state so the screen always has its h1 (ui/DESIGN.md §10).
  const header = (
    <PageHeader
      title="Pembelian"
      lede="Riwayat pesanan dan kwitansi akunmu."
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <Loading label="Memuat pembelian…" />
      </>
    );
  }
  if (error) {
    return (
      <>
        {header}
        <ErrorState error={error} onRetry={reload} />
      </>
    );
  }

  const rows = (orders ?? []).map(toRow);

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      {header}

      {rows.length === 0 ? (
        <EmptyState title="Belum ada pembelian">
          Akun ini belum punya riwayat pesanan.
        </EmptyState>
      ) : (
        <Card padded={false} className="overflow-hidden">
          <TableWrapper label="Riwayat pesanan" className="hidden sm:block">
            <Table className="[&_td:first-child]:pl-5 [&_td:last-child]:pr-5 [&_th:first-child]:pl-5 [&_th:last-child]:pr-5">
              <TableCaption className="sr-only">Riwayat pesanan</TableCaption>
              <TableHeader>
                <tr>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aksi</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className={cn(dataClass, "whitespace-nowrap")}>{r.date}</TableCell>
                    <TableCell className="text-fg-0">{r.product}</TableCell>
                    <TableCell className={cn(dataClass, "text-right whitespace-nowrap text-fg-0")}>
                      {r.total}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <RowAction action={r.action} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>

          <ul aria-label="Riwayat pesanan" className="divide-y divide-line sm:hidden">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-col gap-3 px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-col">
                    <span className="font-medium text-fg-0">{r.product}</span>
                    <span className={cn(dataClass, "text-sm text-fg-2")}>{r.date}</span>
                  </div>
                  <span className={cn(dataClass, "whitespace-nowrap text-fg-0")}>{r.total}</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
                  <StatusBadge status={r.status} />
                  {r.action && <Link to={r.action.to}>{r.action.label}</Link>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
